import { useState, useRef, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { useGetUserQuery } from 'components/profile/userSlice';
import CompleteCertificate from 'components/capstone/capstoneView/CompleteCetificate';
import { useAddCompletedMutation, useGetSuperCapstonesQuery } from '../superCapstoneSlice';
import { useNavigate } from 'react-router-dom';
import StudentReport from './StudentReport';
import { format } from 'date-fns';
import Loading from 'components/global/layout/Loading';
import { PDFExport } from '@progress/kendo-react-pdf';

interface allProps {
  enrollData: any;
  capstoneData: any;
  userProgress: any;
  setShowFeedback: any;
  allTasksCompleted: boolean;
}

const SuperFeedbackCertificate = (props: allProps) => {
  const { enrollData, capstoneData, setShowFeedback, allTasksCompleted } = props;
  const { data } = useGetUserQuery();
  const partnerLogo = data?.partnerId?.logoUrl;
  const partnerName = data?.partnerId?.name;
  const currentCapstoneId = capstoneData?._id; // Get current capstone ID

  const [isOpen, setIsOpen] = useState(false);
  const [addCompleted] = useAddCompletedMutation();
  const navigate = useNavigate();
  const [selectedOption, setSelectedOption] = useState<string | null>(null);

  const pdfExportComponent = useRef<PDFExport>(null);
  const [qrCodeData, setQrCodeData] = useState<string | null>(null);

  // Fetch super capstones
  const { data: superCapstonesData, isLoading, isError, error } =
    useGetSuperCapstonesQuery({ limit: 3 }); // Fetch 3 capstones

  // Log the fetched data
  useEffect(() => {
    if (superCapstonesData) {
      console.log('Fetched Super Capstones:', superCapstonesData);
    }
    if (isError) {
      console.error('Error fetching super capstones:', error);
    }
  }, [superCapstonesData, isError, error]);

  const checkQuizConditions = () => {
    // If not all tasks are completed, show Locked button immediately
    if (!allTasksCompleted) {
      return (
        <button disabled className='bg-red-200 w-fit py-1 px-3 rounded-full'>
          Locked
        </button>
      );
    }

    // Proceed with existing logic if all tasks are completed
    const isFeedbackDone = enrollData?.feedback?.submitTime;

    if (enrollData?.status === 'Completed') {
      return (
        <button
          onClick={() => setIsOpen(true)}
          className='bg-green-500 text-white font-medium py-1 px-4 rounded-full shadow-sm hover:bg-green-600 transition-all'>
          Download Again
        </button>
      );
    }

    if (isLocked) {
      return (
        <button disabled className='bg-red-200 w-fit py-1 px-3 rounded-full'>
          Locked
        </button>
      );
    }
    // isFeedbackDone
    if (isFeedbackDone) {
      return (
        <button
          onClick={() => {
            addCompleted({ enrollId: enrollData?._id });
            setIsOpen(true);
          }}
          className='bg-green-500 text-white font-medium py-1 px-4 rounded-full shadow-sm hover:bg-green-600 transition-all'>
          Get Certificate
        </button>
      );
    }

    return (
      <button
        onClick={() => {
          setShowFeedback(true);
          navigate(
            `/supercapstone/${props.enrollData.superCapstoneId._id}/feedback`
          );
        }}
        className='bg-yellow-400 text-black font-medium py-1 px-4 rounded-full shadow-sm hover:bg-yellow-500 transition-all'>
        Give Feedback
      </button>
    );
  };

  const getFeedbackUnlockDate = () => {
    if (!enrollData) return null;

    if (enrollData.quiz?.length) {
      const latestQuiz = [...enrollData.quiz].sort(
        (a, b) =>
          new Date(b.finishTime).getTime() - new Date(a.finishTime).getTime()
      )[0];
      if (latestQuiz?.finishTime) {
        return format(new Date(latestQuiz.finishTime), 'MMMM d, yyyy');
      }
    }

    if (enrollData.tasks?.length) {
      const latestTask = [...enrollData.tasks].sort(
        (a, b) =>
          new Date(b.submitTime).getTime() - new Date(a.submitTime).getTime()
      )[0];
      if (latestTask?.submitTime) {
        return format(new Date(latestTask.submitTime), 'MMMM d, yyyy');
      }
    }

    return null;
  };

  // Check if prerequisites are met based on unlock date
  const feedbackUnlockDate = getFeedbackUnlockDate();
  const isLocked = !feedbackUnlockDate && enrollData?.status !== 'Completed';

  return (
    <>
      <tr className='font-semibold text-sm md:text-base text-gray-700'>
        <td className='border-l border-b border-gray-300 md:px-4 py-3'>
          Feedback and Certificate
        </td>
        <td className='border-b border-gray-300'></td>
        <td className='border-b border-gray-300'></td>
        <td className='border border-gray-300 text-left md:px-4 py-3'>
          {checkQuizConditions()}
        </td>
      </tr>

      <Dialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        className='relative z-50'>
        <div
          className='fixed inset-0 bg-black/40 backdrop-blur-sm'
          aria-hidden='true'
        />
        <div className='fixed inset-0 overflow-y-auto'>
          <div className='flex min-h-full items-center justify-center p-4'>
            {selectedOption === null ? (
              <Dialog.Panel className='mx-auto max-w-5xl rounded-2xl bg-gradient-to-br from-white to-gray-100 p-10 shadow-2xl'>
                <Dialog.Title className='text-4xl font-bold text-center text-pink-600'>
                  Congratulations!
                </Dialog.Title>

                <div className='grid md:grid-cols-2 gap-10 mt-8'>
                  <div className='flex flex-col items-center bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow duration-300'>
                    <span className='text-5xl mb-4'>🏅</span>
                    <h3 className='text-xl font-semibold mb-3 text-gray-800'>
                      Certificate
                    </h3>
                    <button
                      onClick={() => setSelectedOption('certificate')}
                      className='bg-indigo-600 text-white px-6 py-2 rounded-lg shadow hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-opacity-50 transition duration-300'>
                      View Certificate
                    </button>
                  </div>

                  <div className='flex flex-col items-center bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow duration-300'>
                    <span className='text-5xl mb-4'>📄</span>
                    <h3 className='text-xl font-semibold mb-3 text-gray-800'>
                      Capstone Report
                    </h3>
                    <button
                      onClick={() => setSelectedOption('report')}
                      className='bg-indigo-600 text-white px-6 py-2 rounded-lg shadow hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-opacity-50 transition duration-300'>
                      View Report
                    </button>
                  </div>
                </div>

                <div className='mt-10'>
                  <p className='text-center text-xl font-semibold text-gray-700 mb-6'>
                    Completed Supercapstone <br />
                    Here's what's Next:{' '}
                  </p>

                  <div className='grid grid-cols-1 md:grid-cols-2 gap-6 text-sm'>
                    <div className='bg-white rounded-lg p-5 shadow'>
                      <h4 className='text-center text-indigo-700 font-semibold text-lg mb-3'>
                        Craft Your Presence
                      </h4>
                      <p className='text-gray-600 text-center text-xs mb-4'>
                        Translate your SuperCapstone experience into a
                        compelling narrative.
                      </p>
                      <ul className='space-y-2 text-center text-gray-800'>
                        <li>
                          <a
                            href='https://drive.google.com/file/d/1adqAw-X92do3gHJkcVItFnCWANgRutQI/view?usp=sharing'
                            target='_blank'
                            rel='noopener noreferrer'
                            className='hover:text-indigo-600 hover:underline'>
                            🔗 Stand out on LinkedIn
                          </a>
                        </li>
                        <li>
                          <a
                            href='https://drive.google.com/file/d/1ZT5zFhWdtjb5xWG9hnTBY0jJCwxevX1E/view?usp=sharing'
                            target='_blank'
                            rel='noopener noreferrer'
                            className='hover:text-indigo-600 hover:underline'>
                            📄 Add to CV
                          </a>
                        </li>
                        <li>
                          <a
                            href='https://drive.google.com/file/d/1vWZDBNV-5meW3GOzTW0ZZwf29BoGwSOV/view?usp=sharing'
                            target='_blank'
                            rel='noopener noreferrer'
                            className='hover:text-indigo-600 hover:underline'>
                            🎤 Include in Interviews
                          </a>
                        </li>
                      </ul>
                    </div>

                    <div className='bg-white rounded-lg p-5 shadow'>
                      <h4 className='text-center text-indigo-700 font-semibold text-lg mb-3'>
                        Recommended Capstones
                      </h4>
                      <p className='text-gray-600 text-center text-xs mb-4 italic'>
                        Take your skills to the next level by exploring these.
                      </p>
                      <div className='mt-4'>
                        {isLoading && <Loading />}
                        {isError && (
                          <p className='text-center text-red-500'>
                            Error loading recommendations.
                          </p>
                        )}
                        {superCapstonesData && (
                          <ul className='space-y-2 text-center text-gray-800'>
                            {superCapstonesData.data
                              ?.filter((cap: any) => cap._id !== currentCapstoneId) // Filter out current capstone
                              .slice(0, 2) // Take the first 2 remaining
                              .map((capstone: any) => (
                                <li
                                  key={capstone._id}
                                  className='hover:text-indigo-600 hover:underline cursor-pointer'>
                                  <a
                                    href={`/supercapstone/${capstone._id}`}
                                    target="_blank"
                                    rel="noopener noreferrer">
                                    {capstone.title}
                                  </a>
                                </li>
                              ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className='text-center mt-8'>
                  <button
                    onClick={() => setIsOpen(false)}
                    className='px-8 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition duration-300'>
                    Close
                  </button>
                </div>
              </Dialog.Panel>
            ) : (
              <Dialog.Panel className='mx-auto max-w-3xl rounded-xl bg-white shadow-xl'>
                {selectedOption === 'certificate' ? (
                  <CompleteCertificate
                    partnerName={partnerName}
                    partnerLogo={partnerLogo}
                    user={data}
                    capstoneData={capstoneData}
                    setIsOpen={setIsOpen}
                  />
                ) : (
                  <div className='p-6'>
                    <StudentReport
                      enrollmentDate={
                        enrollData?.enrollmentDate
                          ? format(
                              new Date(enrollData.enrollmentDate),
                              'MMMM d, yyyy'
                            )
                          : enrollData?.createdAt
                          ? format(
                              new Date(enrollData.createdAt),
                              'MMMM d, yyyy'
                            )
                          : 'Not available'
                      }
                      projectData={capstoneData}
                      userData={data}
                      feedbackUnlockDate={feedbackUnlockDate}
                      enrollData={enrollData}
                    />
                  </div>
                )}
                <div className='p-4 border-t text-right'>
                  <button
                    onClick={() => setSelectedOption(null)}
                    className='text-blue-600 hover:underline'>
                    ← Back to options
                  </button>
                </div>
              </Dialog.Panel>
            )}
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default SuperFeedbackCertificate;
